import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useRegister, useResendVerification } from '@/api/hooks/usePasskeys';
import { ApiError } from '@/api/client';
import { LogoFull } from '@/components/LogoFull';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { extractValidationErrors } from '@/utils/errorHandler';
import { usePageTitle } from '@/hooks/usePageTitle';
import { trackAnonymous } from '@/components/PostHogTracker';
import { BanknotesIcon, ChartBarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const FEATURES = [
    {
        icon: BanknotesIcon,
        title: 'Every account in one place',
        description: 'Bank accounts, investments, loans and property, tracked side by side.',
    },
    {
        icon: ChartBarIcon,
        title: 'Watch your net worth move',
        description: 'See how your assets and debts change over time, month by month.',
    },
    {
        icon: ShieldCheckIcon,
        title: 'Your data, secured',
        description: 'Passkey sign-in and encrypted storage. No bank credentials required.',
    },
];

export function RegisterPage() {
    usePageTitle('Sign Up');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [faxNumber, setFaxNumber] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    const registerMutation = useRegister();
    const resendMutation = useResendVerification();

    const isSubmitting = registerMutation.isPending;
    const isResending = resendMutation.isPending;

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setErrors({});
        setGeneralError(null);

        // Client-side validation
        const newErrors: Record<string, string> = {};

        if (!email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(password)) {
            newErrors.password = 'Password must contain an uppercase letter';
        } else if (!/[a-z]/.test(password)) {
            newErrors.password = 'Password must contain a lowercase letter';
        } else if (!/\d/.test(password)) {
            newErrors.password = 'Password must contain a number';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            await registerMutation.mutateAsync({ email, password, faxNumber });
            trackAnonymous('user_registered');
            setSuccess(true);
        } catch (err) {
            if (err instanceof ApiError) {
                const validationErrors = extractValidationErrors(err);
                if (Object.keys(validationErrors).length > 0) {
                    setErrors(validationErrors);
                    trackAnonymous('register_failed', {
                        reason: 'validation',
                        fields: Object.keys(validationErrors),
                    });
                } else {
                    // err.message is only "API request failed: <status>". The server's text is in err.data.
                    const serverMessage = (err.data as { error?: string } | undefined)?.error;
                    setGeneralError(serverMessage || 'Registration failed. Please try again.');
                    trackAnonymous('register_failed', { reason: 'server_error', status: err.status });
                }
            } else {
                setGeneralError('An unexpected error occurred');
                trackAnonymous('register_failed', { reason: 'unknown' });
            }
        }
    }

    async function handleResendVerification() {
        setResendSuccess(false);
        try {
            await resendMutation.mutateAsync({ email });
            setResendSuccess(true);
        } catch {
            // Silent fail - don't reveal if email exists
            setResendSuccess(true);
        }
    }

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/" aria-label="Netfiscus home">
                            <LogoFull className="h-10 w-auto mx-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Check your email
                        </h1>

                        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            We've sent a verification link to <strong>{email}</strong>. Please check your inbox and click the link to activate your account.
                        </div>

                        <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            The verification link will expire in 24 hours.
                        </p>

                        {resendSuccess && (
                            <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                                Verification email resent.
                            </div>
                        )}

                        <div className="space-y-4">
                            <Button
                                type="button"
                                variant="secondary"
                                className="w-full"
                                size="lg"
                                onClick={handleResendVerification}
                                disabled={isResending}
                                isLoading={isResending}
                            >
                                {isResending ? 'Sending...' : 'Resend verification email'}
                            </Button>

                            <Link to="/login" className="block">
                                <Button type="button" className="w-full" size="lg">
                                    Go to login
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid min-h-screen bg-gray-50 dark:bg-dark lg:grid-cols-2">
            <div className="order-2 flex flex-col justify-center bg-primary px-8 py-12 text-white lg:order-1 lg:px-16">
                <div className="mx-auto w-full max-w-md">
                    <a href="https://netfiscus.com" aria-label="Netfiscus website" className="dark mb-8 inline-block">
                        <LogoFull className="h-9 w-auto" />
                    </a>

                    <h1 className="mb-4 text-3xl font-bold leading-tight">
                        Track your net worth in one place
                    </h1>
                    <p className="mb-10 text-gray-300">
                        Netfiscus is a personal net worth tracker. Record your accounts, assets
                        and debts, and follow how your finances change over time &mdash; for free.
                    </p>

                    <ul className="space-y-6">
                        {FEATURES.map(({ icon: Icon, title, description }) => (
                            <li key={title} className="flex gap-4">
                                <Icon className="h-6 w-6 shrink-0 text-accent" aria-hidden="true" />
                                <div>
                                    <p className="font-semibold">{title}</p>
                                    <p className="text-sm text-gray-300">{description}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="order-1 flex items-center justify-center p-8 lg:order-2">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center lg:hidden">
                        <a href="https://netfiscus.com" aria-label="Netfiscus website">
                            <LogoFull className="h-10 w-auto mx-auto" />
                        </a>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Create your account
                        </h2>
                        <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            Start managing your finances today
                        </p>

                        {generalError && (
                            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                                {generalError}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <input
                                type="text"
                                name="fax_number"
                                value={faxNumber}
                                onChange={(e) => setFaxNumber(e.target.value)}
                                autoComplete="off"
                                tabIndex={-1}
                                style={{ position: 'absolute', left: '-9999px' }}
                                aria-hidden="true"
                            />
                            <Input
                                type="email"
                                id="email"
                                label="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@example.com"
                                error={errors.email}
                                autoComplete="email"
                            />

                            <Input
                                type="password"
                                id="password"
                                label="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="Create a strong password"
                                error={errors.password}
                                helperText={!errors.password ? '8+ characters with uppercase, lowercase, and number' : undefined}
                                autoComplete="new-password"
                            />

                            <Input
                                type="password"
                                id="confirmPassword"
                                label="Confirm Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                placeholder="Re-enter your password"
                                error={errors.confirmPassword}
                                autoComplete="new-password"
                            />

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                                className="w-full"
                                size="lg"
                            >
                                {isSubmitting ? 'Creating account...' : 'Create Account'}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                Already have an account?{' '}
                            </span>
                            <Link
                                to="/login"
                                className="text-sm font-medium text-gray-600 underline hover:no-underline dark:text-gray-300"
                            >
                                Sign in
                            </Link>
                        </div>
                    </div>

                    <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Securely manage your financial data
                    </p>
                </div>
            </div>
        </div>
    );
}
